import { Type } from 'typebox';
import { Value } from 'typebox/value';
import { AnnouncementEntry } from './announcement-entry.js';
import { ActivityEntry } from './activity-entry.js';
import { createFallbackStringEnumCodec } from '../../utils/fallback-string-enum-codec.js';
import { BathroomEntry } from './bathroom-entry.js';
import { TypeBoxShortIsoCalendarDateFormat } from '../../utils/register-typebox-short-iso-calendar-date-format.js';
import { isRecord } from '../../utils/is-record.js';
import { DailyReportEntry, DailyReportEntryTypes } from './daily-report-entry.js';
import { DocumentationEntry } from './documentation-entry.js';
import { FoodEntry } from './food-entry.js';
import { HealthEntry } from './health-entry.js';
import { MealDistributionEntry } from './meal-distribution-entry.js';
import { MediaEntry } from './media-entry.js';
import { MedicationEntry } from './medication-entry.js';
import { MedicationNoteEntry } from './medication-note-entry.js';
import { NapEntry } from './nap-entry.js';
import { NoteEntry } from './note-entry.js';
import { SdcAssessmentEntry } from './sdc-assessment-entry.js';
import { SkinApplicationEntry } from './skin-application-entry.js';
import { TeachActivityEntry } from './teach-activity-entry.js';
import { TeachDayHeaderEntry } from './teach-day-header-entry.js';
export const DailyReportTypes = {
    location: 'location',
    group: 'group',
    individual: 'individual',
    unknown: 'unknown'
};
export const DailyReportType = Type.Union([
    Type.Literal(DailyReportTypes.location),
    Type.Literal(DailyReportTypes.group),
    Type.Literal(DailyReportTypes.individual),
    Type.Literal(DailyReportTypes.unknown)
]);
export const DailyReportTypeCodec = createFallbackStringEnumCodec(DailyReportTypes, DailyReportTypes.unknown);
export const AnyDailyReportEntry = Type.Codec(Type.Unknown())
    .Decode((value) => {
    if (!isRecord(value)) {
        throw new Error('Daily report entry payload must be an object');
    }
    switch (value.typeName) {
        case DailyReportEntryTypes.announcement:
            return Value.Decode(AnnouncementEntry, value);
        case DailyReportEntryTypes.activity:
            return Value.Decode(ActivityEntry, value);
        case DailyReportEntryTypes.bathroom:
            return Value.Decode(BathroomEntry, value);
        case DailyReportEntryTypes.documentation:
            return Value.Decode(DocumentationEntry, value);
        case DailyReportEntryTypes.food:
            return Value.Decode(FoodEntry, value);
        case DailyReportEntryTypes.health:
            return Value.Decode(HealthEntry, value);
        case DailyReportEntryTypes.mealDistribution:
            return Value.Decode(MealDistributionEntry, value);
        case DailyReportEntryTypes.media:
            return Value.Decode(MediaEntry, value);
        case DailyReportEntryTypes.medication:
            return Value.Decode(MedicationEntry, value);
        case DailyReportEntryTypes.medicationNote:
            return Value.Decode(MedicationNoteEntry, value);
        case DailyReportEntryTypes.nap:
            return Value.Decode(NapEntry, value);
        case DailyReportEntryTypes.note:
            return Value.Decode(NoteEntry, value);
        case DailyReportEntryTypes.sdcAssessment:
            return Value.Decode(SdcAssessmentEntry, value);
        case DailyReportEntryTypes.skinApplication:
            return Value.Decode(SkinApplicationEntry, value);
        case DailyReportEntryTypes.teachActivity:
            return Value.Decode(TeachActivityEntry, value);
        case DailyReportEntryTypes.teachDayHeader:
            return Value.Decode(TeachDayHeaderEntry, value);
        default:
            return Value.Decode(DailyReportEntry, value);
    }
})
    .Encode((value) => {
    switch (value.typeName) {
        case DailyReportEntryTypes.announcement:
            return Value.Encode(AnnouncementEntry, value);
        case DailyReportEntryTypes.activity:
            return Value.Encode(ActivityEntry, value);
        case DailyReportEntryTypes.bathroom:
            return Value.Encode(BathroomEntry, value);
        case DailyReportEntryTypes.documentation:
            return Value.Encode(DocumentationEntry, value);
        case DailyReportEntryTypes.food:
            return Value.Encode(FoodEntry, value);
        case DailyReportEntryTypes.health:
            return Value.Encode(HealthEntry, value);
        case DailyReportEntryTypes.mealDistribution:
            return Value.Encode(MealDistributionEntry, value);
        case DailyReportEntryTypes.media:
            return Value.Encode(MediaEntry, value);
        case DailyReportEntryTypes.medication:
            return Value.Encode(MedicationEntry, value);
        case DailyReportEntryTypes.medicationNote:
            return Value.Encode(MedicationNoteEntry, value);
        case DailyReportEntryTypes.nap:
            return Value.Encode(NapEntry, value);
        case DailyReportEntryTypes.note:
            return Value.Encode(NoteEntry, value);
        case DailyReportEntryTypes.sdcAssessment:
            return Value.Encode(SdcAssessmentEntry, value);
        case DailyReportEntryTypes.skinApplication:
            return Value.Encode(SkinApplicationEntry, value);
        case DailyReportEntryTypes.teachActivity:
            return Value.Encode(TeachActivityEntry, value);
        case DailyReportEntryTypes.teachDayHeader:
            return Value.Encode(TeachDayHeaderEntry, value);
        default:
            return Value.Encode(DailyReportEntry, value);
    }
});
export const DailyReport = Type.Object({
    date: Type.String({ format: TypeBoxShortIsoCalendarDateFormat }),
    subjectType: DailyReportTypeCodec,
    subjectKey: Type.String({ minLength: 1 }),
    groupKey: Type.Optional(Type.String({ minLength: 1 })),
    locationKey: Type.String({ minLength: 1 }),
    creatorKey: Type.Optional(Type.String({ minLength: 1 })),
    startTimestamp: Type.Optional(Type.Number()),
    clientUpdateTimestamp: Type.Number(),
    batch: Type.Boolean(),
    stage: Type.Optional(Type.String()),
    published: Type.Boolean(),
    entries: Type.Array(AnyDailyReportEntry),
    teachers: Type.Optional(Type.Array(Type.String({ minLength: 1 })))
});
